/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Widget Domain Security & Whitelisting Tab Component (Task 10.3.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useWidgetStudioStore } from '../../stores/useWidgetStudioStore';
import { useI18n } from '../../hooks/useI18n';
import { Shield, Plus, X, Globe, AlertTriangle, CheckCircle2 } from 'lucide-react';

export const WidgetSecurityTab: React.FC = () => {
  const { t } = useI18n();
  const { config, addWhitelistedDomain, removeWhitelistedDomain } = useWidgetStudioStore();
  const [newDomainInput, setNewDomainInput] = useState('');

  const handleAddDomain = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomainInput.trim()) return;
    addWhitelistedDomain(newDomainInput);
    setNewDomainInput('');
  };

  return (
    <div className="space-y-6">
      {/* 1. Header & Overview */}
      <div className="space-y-2">
        <h3 className="text-sm font-heading font-bold text-foreground flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" />
          <span>{t('widget.securityHeading')}</span>
        </h3>
        <p className="text-xs text-foreground-muted leading-relaxed">
          {t('widget.securityDesc')}
        </p>
      </div>

      {/* 2. Add Domain Input */}
      <form onSubmit={handleAddDomain} className="flex gap-2">
        <div className="relative flex-1">
          <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
          <input
            type="text"
            value={newDomainInput}
            onChange={(e) => setNewDomainInput(e.target.value)}
            placeholder={t('widget.domainPlaceholder')}
            className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 font-mono"
          />
        </div>
        <button
          type="submit"
          disabled={!newDomainInput.trim()}
          className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-1.5 flex-shrink-0 shadow-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>{t('widget.addDomainBtn')}</span>
        </button>
      </form>

      {/* 3. Approved Domains List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-heading font-bold text-foreground uppercase tracking-wider">
            {t('widget.whitelistedDomainsList', { count: config.whitelistedDomains.length })}
          </span>
          <span className="text-[11px] text-foreground-muted">
            {t('widget.instantEdgeDeploy')}
          </span>
        </div>

        {config.whitelistedDomains.length === 0 ? (
          <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 text-amber-600 dark:text-amber-400 text-xs flex items-center gap-2.5">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              {t('widget.emptyWhitelistWarning')}
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {config.whitelistedDomains.map((domain) => (
              <div
                key={domain}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono text-foreground shadow-sm group hover:border-primary/40 transition-colors"
              >
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                <span>{domain}</span>
                <button
                  type="button"
                  onClick={() => removeWhitelistedDomain(domain)}
                  className="text-foreground-muted hover:text-rose-500 transition-colors ml-1"
                  title={t('common.delete')}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <hr className="border-surface-border" />

      {/* 4. Tips & Wildcard Rules */}
      <div className="p-4 rounded-xl border border-surface-border bg-surface text-xs space-y-2">
        <h4 className="font-bold text-foreground">{t('widget.securityNotesTitle')}</h4>
        <ul className="list-disc list-inside space-y-1 text-foreground-muted text-[11px] leading-relaxed">
          <li>{t('widget.securityNote1')}</li>
          <li>{t('widget.securityNote2')}</li>
          <li>{t('widget.securityNote3')}</li>
        </ul>
      </div>
    </div>
  );
};
